import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["$verticalSeparation"],
  _excluded2 = ["children", "Component", "formControlUse"],
  _excluded3 = ["hideEditIcon", "isStatic", "label", "overlayContent", "readOnly", "verticalSeparation"],
  _excluded4 = ["hideEditIcon", "isStatic", "label"];
import UIFloatingFormControl from 'UIComponents/form/UIFloatingFormControl';
import { propertyLabelTranslator } from 'property-translator/propertyTranslator';
import { useProperty } from '../hooks/useProperty';
import { useGetIsUngated } from '../hooks/useGetIsUngated';
import styled from 'styled-components';
import { isFile } from 'customer-data-objects/property/PropertyIdentifier';
import { useIsUngatedForDisplayOptimizedFormControl } from '../hooks/useIsUngatedForDisplayOptimizedFormControl';
import FormControlAdapter from '../../adapters/FormControl';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getLabel = property => {
  return property.hubspotDefined ? propertyLabelTranslator(property.label) : property.label;
};
const StyledDivOverlayWrapper = styled.div.withConfig({
  displayName: "FormControl__StyledDivOverlayWrapper",
  componentId: "sc-1a3q64u-0"
})(["height:100%;"]);

/**
 * This is a temporary workaround to support transient props,
 * as well as a short-term solution for teams facing spacing
 * issues after dropping support for the className prop.
 *
 * https://styled-components.com/docs/api#transient-props
 * https://git.hubteam.com/HubSpot/Frontend/issues/1770
 */
const StyledUIFloatingFormControl = styled(
// eslint-disable-next-line @typescript-eslint/no-unused-vars
_ref => {
  let props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(UIFloatingFormControl, Object.assign({}, props));
}).withConfig({
  displayName: "FormControl__StyledUIFloatingFormControl",
  componentId: "sc-1a3q64u-1"
})([".private-form__control__overlay{background:transparent !important;box-shadow:none !important;}", ""], ({
  $verticalSeparation
}) => $verticalSeparation === 'flush' && `
      margin-top: 0px !important;
      margin-bottom: 0px !important;
      padding-top: 0px !important;
      padding-bottom: 0px !important;
    `);
const FormControl = _ref2 => {
  let {
      children,
      Component = FormControlAdapter,
      formControlUse = 'default'
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const property = useProperty();
  const getIsUngated = useGetIsUngated();
  const isUngatedForDisplayOptimizedFormControl = useIsUngatedForDisplayOptimizedFormControl();
  if (formControlUse === 'inline' || formControlUse === 'table-cell' || !Component) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  const hasLabelGate = getIsUngated('CRM:Properties:DefaultLabel');
  const defaultLabel = hasLabelGate ? getLabel(property) : undefined;
  if (formControlUse === 'floating' || Component === UIFloatingFormControl) {
    const {
        hideEditIcon,
        isStatic,
        label = defaultLabel,
        overlayContent,
        readOnly,
        verticalSeparation
      } = rest,
      other = _objectWithoutPropertiesLoose(rest, _excluded3);
    if (isUngatedForDisplayOptimizedFormControl) {
      return /*#__PURE__*/_jsx(FormControlAdapter, Object.assign({
        displayOptimized: true,
        hideDisplayOptimizedBorderIndicator: isStatic || readOnly,
        label: label,
        verticalSeparation: verticalSeparation
      }, other, {
        children: children
      }));
    }
    const overlayContentWithWrapper = isFile(property) ? /*#__PURE__*/_jsx(StyledDivOverlayWrapper, {
      children: overlayContent
    }) : overlayContent;
    return /*#__PURE__*/_jsx(StyledUIFloatingFormControl, Object.assign({
      $verticalSeparation: verticalSeparation,
      hideEditIcon: hideEditIcon,
      isStatic: isStatic,
      "data-test-id": UIFloatingFormControl.name,
      label: label,
      overlayContent: overlayContentWithWrapper,
      readOnly: readOnly
    }, other, {
      children: children
    }));
  }
  const {
      label = defaultLabel
    } = rest,
    other = _objectWithoutPropertiesLoose(rest, _excluded4);
  return /*#__PURE__*/_jsx(Component, Object.assign({
    "data-test-id": Component.name,
    label: label,
    isTransitional: isUngatedForDisplayOptimizedFormControl
  }, other, {
    children: children
  }));
};
export default FormControl;